// Standard library imports
import React, { useEffect, useState } from 'react';
// External library imports
import { toast } from 'react-toastify';
import { UilPen } from '@iconscout/react-unicons';
import moment from 'moment';
import { get as lodashGet, set as lodashSet, keys as lodashKeys } from 'lodash';

// Internal module imports
import { useLoader, useUserRole } from '../../../../../hooks';
import DatePicker from '../../../../../components/Inputs/DatePicker/DatePicker';
import Table from '../../../../../components/Table/Table';
import Typography from '../../../../../components/Typography/Typography';
import Checkbox from '../../../../../components/Inputs/Checkbox/Checkbox';
import ModalComponent from '../../../../../components/ModalComponent/ModalComponent';
import { momentTimeFormater } from '../../../../../utils/dateHelper';
import allDeviceClasses from '../../../../../styles/AllDevices.module.css';
import bidClasses from '../../Bidding.module.css';
import IconComponent from '../../../../../components/Icon/Icon';
import { RootBundleBiddingService } from '../../../../../services/BiddingService/Root/BundleBiddingService';
import { AdminBundleBiddingService } from '../../../../../services/BiddingService/Admin/BundleBiddingService';
import { RootBidRequestService } from '../../../../../services/BiddingService/Root/BidRequestService';
import { AdminBidRequestService } from '../../../../../services/BiddingService/Admin/BidRequestService';
import { CreateBundleBid } from './CreateBundleBid';

export const CreateBulkBundleBid = ({ data, closeModal, getBids }) => {
    const { isRootUser } = useUserRole();
    const service = isRootUser ? RootBundleBiddingService : AdminBundleBiddingService;
    const serviceBidRequest = isRootUser ? RootBidRequestService : AdminBidRequestService;
    const { adminId, customerId, market, bidDate } = data;
    const isFRR = market.includes('aFRR') || market.includes('mFRR');
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useState(moment(bidDate).toDate());
    const [bidsData, setBidsData] = useState([]);
    const [alreadyPresentBids, setAlreadyPresentBids] = useState([]);
    const [editBidModal, setEditBidModal] = useState({ status: false, bid: null });
    const [bidRequestsInstanceIds, setBidRequestsInstanceIds] = useState([]);

    useEffect(() => {
        if (adminId && customerId && market) getBidsForBulkBid();
    }, []);

    useEffect(() => {
        getBidRequestForDate();
    }, [date]);

    useEffect(() => {
        if (bidRequestsInstanceIds.length && bidsData.length) {
            const temp = bidsData?.map((bundle) => {
                let bidRequest = 'No';
                bundle?.bundleBid?.bids?.map((bid) => {
                    if (bidRequestsInstanceIds?.includes(bid.instanceId)) bidRequest = 'Yes';
                });
                bundle.bidRequest = bidRequest;
                return bundle;
            });
            setBidsData(temp);
        }
    }, [bidRequestsInstanceIds]);

    const getBidsForBulkBid = () => {
        const params = {
            market: market,
            bidDate: moment(date).format('YYYY-MM-DD'),
            adminId: adminId,
            customerId: customerId,
        };

        service.GetBidsForBulkBids(params, startLoader, handleBidsSuccess, handleError, stopLoader);
    };

    const handleBidsSuccess = ({ data }) => {
        const processedData = lodashGet(data, ['data'], []).map((item) => {
            return {
                ...item,
                select: getCheckbox(item),
                customerName: item.customerName,
                bundleName: item.instanceBundleName,
                averageCapacity: item.averageCapacity !== null ? item.averageCapacity : '--',
                action: getAction(item),
                bidRequest: 'No',
                style: !item.isBidPresent ? { background: '#FFE3E3', color: '#E25657' } : {},
            };
        });
        setBidsData(processedData);
    };

    const getCheckbox = (item) => {
        return (
            <div className={bidClasses.Flex} style={{ justifyContent: 'center' }}>
                <Checkbox onChange={(isChecked) => getCheckedBid(isChecked, item)} checked={item.isChecked || false} />
            </div>
        );
    };

    const getAction = (item) => {
        return (
            <div
                onClick={() =>
                    setEditBidModal({
                        status: true,
                        data: {
                            instanceBundle: {
                                label: item.instanceBundleName,
                                value: item.instanceBundleId,
                                pairs: item.pairs || [],
                            },
                            market: item.market,
                            bidDate: date,
                            indivisualBidsData: item.indivisualBidsData,
                            ...(market.includes('aFRR') || market.includes('mFRR') ? { cost: item?.cost } : {}),
                        },
                    })
                }
            >
                <IconComponent icon={UilPen} tooltip={'Edit'} />
            </div>
        );
    };

    const getCheckedBid = (checked, bidsData) => {
        const hasCapacity = lodashKeys(lodashGet(bidsData, ['indivisualBidsData', 'capacity'], {})).length;
        const hidePrice = isFRR && lodashGet(bidsData, ['pairs'], []).some((p) => p.energyMarketOnly);
        const hasPrice = hidePrice || lodashGet(bidsData, ['indivisualBidsData', 'price'], []).length;

        if (!hasCapacity || !hasPrice) {
            toast.error("Can't select empty data bids");
            return;
        }

        setBidsData((prev) =>
            prev.map((item) => {
                if (item.instanceBundleId === bidsData.instanceBundleId) {
                    item.isChecked = checked;
                    item.select = getCheckbox(item);
                    return item;
                } else {
                    return item;
                }
            })
        );
    };

    const handleSubmit = () => {
        const payload = {
            market: market,
            bidDate: momentTimeFormater(date).format('YYYY-MM-DD'),
            bidsData: [],
        };
        bidsData.map((el) => {
            if (el.isChecked)
                payload.bidsData.push({
                    indivisualBidsData: el.indivisualBidsData,
                    instanceBundleId: el.instanceBundleId,
                });
        });
        service.CreateBulkBids(payload, startLoader, handleCreateBidsSuccess, handleError, stopLoader);
    };

    const handleCreateBidsSuccess = ({ data }) => {
        if (lodashGet(data, ['data', 'message']) === 'Bids already created') {
            toast.error('Bids already created');
            setAlreadyPresentBids(lodashGet(data, ['data', 'alreadyPresentBids']));
        } else {
            toast.success('Bids Created Successfully');
            closeModal();
            getBids();
        }
    };

    const editFunc = (updatedBid) => {
        setBidsData((prev) =>
            prev.map((item) => {
                if (item.instanceBundleId === updatedBid.instanceBundleId) {
                    item.indivisualBidsData = {
                        capacity: updatedBid.capacity,
                        price: updatedBid.price,
                        ...(market.includes('aFRR') || market.includes('mFRR')
                            ? { regulationPrice: updatedBid.regulationPrice }
                            : { reserveBidIdentification: updatedBid.reserveBidIdentification }),
                    };
                    item.action = getAction(item || []);
                    item.select = getCheckbox(item);
                    return item;
                } else return item;
            })
        );
    };

    const getBidRequestForDate = () => {
        const params = {
            market: market,
            date: momentTimeFormater(date).format('YYYY-MM-DD'),
        };

        serviceBidRequest.GetBidRequestForDate(params, startLoader, handleBidRequestSuccess, handleError, stopLoader);
    };

    const handleBidRequestSuccess = ({ data }) => {
        const temp = lodashGet(data, ['data'], []).map((bidRq) => bidRq.instanceId);
        setBidRequestsInstanceIds(temp);
    };

    const handleError = (err) => {
        toast.error(lodashGet(err, ['response', 'data', 'message']));
    };

    return (
        <div>
            <div className={bidClasses.MainModalContainer}>
                <div className={bidClasses.DateContainer}>
                    <Typography content={`Create Bulk Bundle Bid (${market})`} />
                    <div className="modal-date-picker" style={{ width: '8vw' }}>
                        <DatePicker date={date} setDate={setDate} disableFutureDate={false} />
                    </div>
                </div>
                <div className={bidClasses.MainContainer}>
                    <Table
                        head={['Select', 'Customer Name', 'Bundle Name', 'Avg Capacity', 'Bid Request', 'Action']}
                        keys={['select', 'customerName', 'bundleName', 'averageCapacity', 'bidRequest', 'action']}
                        data={bidsData}
                    />
                </div>
                <div>
                    {alreadyPresentBids.length ? (
                        <div>
                            <Typography content="Bid is already created for these instances" size={'14'} />
                            <div className={bidClasses.Flex}>
                                {alreadyPresentBids.map((bid) => (
                                    <div className={bidClasses.InstanceName}>
                                        {bid.name} - {bid.market},
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        ''
                    )}
                </div>
                <div>
                    <div className={allDeviceClasses.ButtonContainer}>
                        <div onClick={closeModal}>
                            <button type="button" className="btn-secondary">
                                Cancel
                            </button>
                        </div>
                        <div onClick={handleSubmit}>
                            <button type="submit" className="btn-primary">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {editBidModal.status && (
                <ModalComponent
                    isOpen={editBidModal.status}
                    setOpen={() => {
                        setEditBidModal({ status: false, data: null });
                    }}
                >
                    {editBidModal.data && (
                        <CreateBundleBid
                            data={editBidModal.data}
                            closeModal={() => setEditBidModal({ status: false, data: null })}
                            editBid={true}
                            existingBid={editBidModal.data.indivisualBidsData}
                            editFunc={editFunc}
                        />
                    )}
                </ModalComponent>
            )}
        </div>
    );
};
